import { AuthContext, AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { useMutation } from '@tanstack/react-query';
import { ChannelsAccessApiClient } from './channels';
import {
  ALLOWED_OUTPUT_COUNT_PER_PROVIDER,
  Destination,
  DestinationMetadataApiClient,
  DestinationsStorageApiClient,
} from '../services/DestinationsApiClient';
import { ScopedStorage } from '../services/ScopedStorageService';
import { getConnectPlatformScopedStorageItemId } from '../utils/auth';
import { allowedConnectOptionsSchema } from '../constants/common';
import { navigateInModal } from '../utils/navigateInModal';
import { StreamingStart } from '../services/StreamingStartService';
import { useRef } from 'react';
import { showToast } from '../components/Toast';
import { Analytics } from '../services/AnalyticsService';
import { AbortedError } from '../types/errors';

export async function getPlatformAccessToken({
  authContextProvider,
  serviceEnvironment,
  channel,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  channel: { _id: string };
}) {
  const channelsAccessApiClient = new ChannelsAccessApiClient({
    authContextProvider,
    serviceEnvironment,
  });

  const { token } = await channelsAccessApiClient.impersonateChannel({
    channelId: channel._id,
  });

  const tokenV2 = token;

  const sharedChannel = await channelsAccessApiClient.fetchChannelDetails({
    channelId: channel._id,
    token: tokenV2 || token,
  });

  const providerMatch = allowedConnectOptionsSchema.safeParse(sharedChannel.provider);

  if (!providerMatch.success) {
    throw new Error('Invalid provider');
  }

  const { data: provider } = providerMatch;

  const authContext = {
    channel: sharedChannel,
    token: tokenV2 || token,
    tokenType: 'Bearer',
    isAnonymous: false,
    isImpersonated: false,
    profile: {} as never,
    user: {} as never,
  } satisfies AuthContext;

  const channelAuthContextProvider = {
    ...authContext,
    authContext,
    getAuthContext: async () => authContext,
    serviceEnvironment,
    extractTokenFromStorageOptions: () => tokenV2 || token,
  } as unknown as AuthContextProvider;

  const destinationsApiClient = new DestinationsStorageApiClient({
    authContextProvider: channelAuthContextProvider,
    serviceEnvironment,
  });

  await ScopedStorage.writeScopedStorageJsonItem({
    ...getConnectPlatformScopedStorageItemId(),
    content: {
      channelId: sharedChannel._id,
      token,
    },
  });

  const tokenResult = await destinationsApiClient.getPlatformAccessToken({
    provider,
    authContextProvider: channelAuthContextProvider,
  });

  if (tokenResult === null) {
    throw new AbortedError('Output connection aborted');
  }

  const { accessToken } = tokenResult;

  await ScopedStorage.removeScopedStorageJsonItem({
    ...getConnectPlatformScopedStorageItemId(),
  }).catch((err) => {
    console.log('err', err);
    throw err;
  });

  return {
    accessToken,
    channel: sharedChannel,
    authContextProvider: channelAuthContextProvider,
  };
}

export async function enableStreamDestination({
  authContextProvider,
  serviceEnvironment,
  streamDestination,
  source,
  outputId,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  streamDestination: Destination | null;
  source: { id: string };
  outputId?: string;
}) {
  const { isStreamingActive } = await StreamingStart.getStreamingStatus();

  const destinationsApiClient = new DestinationsStorageApiClient({
    authContextProvider,
    serviceEnvironment,
  });

  const { channel, accessToken } = await getPlatformAccessToken({
    authContextProvider,
    serviceEnvironment,
    channel: { _id: source.id },
  });

  switch (channel.provider) {
    case 'facebook' as never: // a relic from the past
      throw new Error('Platform not supported');
    default:
      break;
  }

  const destinationMetadataApiClient = new DestinationMetadataApiClient({
    authContextProvider,
    serviceEnvironment,
    destinationMetadata: {
      id: channel._id,
      providerId: channel.providerId,
      displayName: channel.displayName,
      provider: channel.provider,
      accessToken,
      expiresAt: new Date(2050, 1, 1).toISOString(),
      refreshToken: '',
      refreshExpiresAt: new Date(2050, 1, 1).toISOString(),
      outputs: [],
    },
  });

  for (let i = 0; i < ALLOWED_OUTPUT_COUNT_PER_PROVIDER[channel.provider]; i++) {
    const current = streamDestination?.outputs?.[i];
    const id = `${channel._id}-${channel.provider}-${i + 1}`;

    if (!current) {
      const output = await destinationMetadataApiClient.addOutput(
        {
          name: channel.displayName,
          isEnabled: outputId ? id === outputId : true,
          streamingSettings: {
            serverUrl: '',
            streamKey: '',
          },
        },
        { notifyOBS: false }
      );

      if (!output) {
        console.warn('Failed to create output', {
          data: JSON.stringify(current),
        });
        throw new Error('Failed to create output');
      }

      continue;
    }

    if (current?.id === outputId) {
      current.isEnabled = true;
    }

    const output = await destinationMetadataApiClient.addOutput(current, { notifyOBS: false });

    if (!output) {
      console.warn('Failed to update output', {
        data: JSON.stringify(current),
      });
      throw new Error('Failed to update output');
    }
  }

  await destinationsApiClient.storeDestination({
    destination: destinationMetadataApiClient.get(),
  });

  Analytics.reportMultiStreamingEvent({
    name: 'streaming_output_enabled',
    fields: [
      ['platform', destinationMetadataApiClient.get().provider],
      ['streaming_outputs_added', destinationMetadataApiClient.get().outputs.length],
    ],
  });

  if (!isStreamingActive) {
    return destinationMetadataApiClient.get();
  }

  // If the user is streaming, open the modal to setup a broadcast title and other metadata.
  const didStartStreaming = await navigateInModal({
    to: '/settings/broadcast-settings',
    search: {
      output_id: outputId,
      mode: 'streams:start',
    },
    title: 'Settings - StreamElements Multi-streaming',
    width: 610,
    height: 880,
  });

  if (didStartStreaming) {
    return destinationMetadataApiClient.get();
  }

  showToast({
    message: 'Stream start aborted.',
    color: 'attention',
    duration: 2000,
  });

  if (!outputId) {
    return destinationMetadataApiClient.get();
  }

  await destinationMetadataApiClient.disableOutputsByIds({ outputIds: [outputId] });

  return destinationMetadataApiClient.get();
}

export function useEnableStreamDestinationMutation({
  authContextProvider,
  serviceEnvironment,
  streamDestination,
  source,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  streamDestination: Destination | null;
  source: { id: string };
}) {
  const streamDestinationRef = useRef(streamDestination);
  streamDestinationRef.current = streamDestination;

  return useMutation({
    mutationKey: ['enable-destination'],
    mutationFn: async ({ outputId }: { outputId: string }) => {
      return enableStreamDestination({
        authContextProvider,
        serviceEnvironment,
        streamDestination: streamDestinationRef.current,
        source,
        outputId,
      });
    },
  });
}
